import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Container, Navbar } from "react-bootstrap";
import { Outlet } from "react-router-dom";
import { Box, Divider, Toolbar, AppBar, Drawer, Typography, Avatar, Paper, Badge, Select, MenuItem, Collapse, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { changeDateFormate } from "../../../global/globalFunctions";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from '@mui/icons-material/Warning';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Menu from "./menu";
import IconMenu from "./IconMenu";
const drawerWidth = 265;


const ResponsiveDrawer = (props) => {
  const { moduleKey, heading, MEData } = useSelector((state) => state.HomePageReducer);
  const { i18n } = useTranslation();
  const [mode] = useState("light");
  const theme = useMemo(() => createTheme({ palette: { mode } }), [mode]);
  const userName = window.localStorage.getItem("userName") || "User";

  const [alerts, setAlerts] = useState([
    { id: 1, show: MEData.isAlertShow ? true : false, severity: "warning", backgroundColor: "#fbc828", message: `Your license will expire on ${changeDateFormate(MEData.date)} . Please renew and complete the payment to continue using the software.` },
    { id: 2, show: false, severity: "info", backgroundColor: "#0288D1", message: "This alert is hidden because show is false." },
    { id: 3, show: false, severity: "error", backgroundColor: "#E68383", message: "Scheduled maintenance tonight at 11 PM. Expect brief downtime." },
  ]);
  const SEVERITY_ICONS = {
    warning: <WarningIcon fontSize="small" />,
    error: <ErrorOutlineIcon fontSize="small" />,
    info: <InfoOutlinedIcon fontSize="small" />,
    success: <CheckCircleOutlineIcon fontSize="small" />,
  };
  const visibleAlerts = alerts.filter((a) => a.show === true);
  const alertBannerHeight = visibleAlerts.length * 48;

  const dismissAlert = (id) =>
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, show: false } : a))
    );

  useEffect(() => {
    const lang = localStorage.getItem("lang") || "en";
    i18n.changeLanguage(lang);
  }, []);


  const permissions = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("permissions")) || []; }
    catch { return []; }
  }, []);

  const handleLanguageChange = (event) => {
    const lang = event.target.value;
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex" }}>

        {/* ================= ALERT BANNER ================= */}
        <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: (t) => t.zIndex.drawer + 200 }}>
          {alerts.map((alert) => (
            <Collapse key={alert.id} in={alert.show} timeout={300} unmountOnExit>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: alert.backgroundColor,
                  color: "#253858",
                  px: 3,
                  py: 0.75,
                  minHeight: 48,
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", color: "#253858", opacity: 0.75 }}>
                    {SEVERITY_ICONS[alert.severity] ?? SEVERITY_ICONS.warning}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: "#253858", lineHeight: 1.4, fontSize: '15px' }}>
                    {alert.message}
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  // onClick={() => dismissAlert(alert.id)}
                  sx={{ color: "#253858", ml: 2, "&:hover": { bgcolor: "rgba(0,0,0,0.1)" } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Collapse>
          ))}
        </Box>

        {/* ================= APP BAR ================= */}
        <AppBar
          position="fixed"
          sx={{
            top: alertBannerHeight,
            width: `calc(100% - ${drawerWidth}px)`,
            ml: `${drawerWidth}px`,
            transition: "top 0.3s ease",
          }}
        >
          <Navbar style={{ paddingLeft: "3%", paddingRight: "3%", backgroundColor: "#81abd3" }}>
            <Container style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Navbar.Brand style={{ color: "white" }}><b>{heading}</b></Navbar.Brand>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Select
                  value={i18n.language}
                  onChange={handleLanguageChange}
                  size="small"
                  sx={{ bgcolor: "white", borderRadius: 1, minWidth: 110 }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="ar">العربية</MenuItem>
                  <MenuItem value="ur">Urdu</MenuItem>
                  <MenuItem value="gr">German</MenuItem>
                </Select>
                <IconMenu />
              </Box>
            </Container>
          </Navbar>
        </AppBar>

        {/* ================= DRAWER ================= */}
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              top: alertBannerHeight,
              height: `calc(100% - ${alertBannerHeight}px)`,
              transition: "top 0.3s ease, height 0.3s ease",
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{ m: 1, p: 1, display: "flex", alignItems: "center", gap: 2, backgroundColor: "#c8dff4", borderRadius: 3 }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              sx={{ "& .MuiBadge-badge": { backgroundColor: "#22c55e", width: 13, height: 13, borderRadius: "50%", border: "2px solid white" } }}
            >
              <Avatar sx={{ bgcolor: "primary.main" }}>{userName[0].toUpperCase()}</Avatar>
            </Badge>
            <Box>
              <Typography fontWeight={600}>{userName}</Typography>
              <Typography variant="caption" color="text.secondary">Admin</Typography>
            </Box>
          </Paper>

          <Divider />
          <Menu moduleKey={moduleKey} heading={heading} permissions={permissions} />
        </Drawer>

        {/* ================= MAIN CONTENT ================= */}
        <Box component="main" sx={{ flexGrow: 1, bgcolor: "background.default", p: 3 }}>
          <Toolbar sx={{ mt: `${alertBannerHeight}px`, transition: "margin 0.3s ease" }} />
          <Outlet />
        </Box>

        {/* ================= FIXED FOOTER ================= */}
        {/* <Box component="footer"
          sx={{
            position: "fixed",
            bottom: 0,
            left: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            height: 36,
            bgcolor: "#f5f7fa",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: (t) => t.zIndex.drawer + 100,
          }}
        >
          <Typography variant="caption" sx={{ color: "#6b7280", fontSize: "13px", letterSpacing: 0.3, }}>
            © {new Date().getFullYear()} <b>Software Hub</b> • All Rights Reserved • ERP System
          </Typography>
        </Box> */}

      </Box>
    </ThemeProvider>
  );
}

export default ResponsiveDrawer;